import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import PageHeader from '../../components/shell/PageHeader';
import Button from '../../components/ui/Button';
import IconButton from '../../components/ui/IconButton';
import StatusPill from '../../components/ui/StatusPill';
import { Avatar } from '../../components/ui/UserCell';
import { Field, Select, TextInput, Textarea } from '../../components/ui/Field';
import { SkeletonBlock } from '../../components/ui/Skeleton';
import Modal from '../../components/overlays/Modal';
import { useOverlays } from '../../components/overlays/OverlayProvider';
import {
  agentDetailsGet,
  agentParamVariable,
  agentServicesGet,
  agentUpdateDetails,
  createAgentService,
  updateAgentServiceDetails,
} from '../../actions/agentAction';
import { phone } from '../../helpers/format';
import './partners.css';

const CURRENCIES = [
  { value: 'INR', label: 'INR' },
  { value: 'USD', label: 'USD' },
];

const SERVICE_STATUSES = [
  { value: 'live', label: 'Live' },
  { value: 'under_review', label: 'Under review' },
  { value: 'on_hold', label: 'On hold' },
];

const EMPTY_SERVICE = {
  title: '',
  short_desc: '',
  long_desc: '',
  duration: 30,
  price: '',
  currency: 'INR',
  status: 'live',
};

/** Multi-select rendered as toggleable chips, for expertise / languages / skills. */
function ChipPicker({ label, options, value, onChange }) {
  return (
    <Field label={label}>
      <div className="sv-chip-picker">
        {options.map((opt) => {
          const active = value.includes(opt);
          return (
            <button
              type="button"
              key={opt}
              className={`sv-chip ${active ? 'is-active' : ''}`}
              onClick={() => onChange(active ? value.filter((v) => v !== opt) : [...value, opt])}
            >
              {opt}
            </button>
          );
        })}
        {options.length === 0 && <span className="sv-chip-picker__empty">No options configured</span>}
      </div>
    </Field>
  );
}

export default function PartnerProfile() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { confirm, toast } = useOverlays();

  const [partner, setPartner] = useState(null);
  const [services, setServices] = useState([]);
  const [params, setParams] = useState({ expertise: [], languages: [], skills: [] });
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState({ bio: '', experience: '', expertise: [], languages: [], skills: [] });
  const [savingProfile, setSavingProfile] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState(EMPTY_SERVICE);
  const [savingService, setSavingService] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [detail, svc] = await Promise.all([
        agentDetailsGet(dispatch, id),
        agentServicesGet(dispatch, id),
      ]);
      setPartner(detail || null);
      setServices(Array.isArray(svc) ? svc : []);
      const p = detail?.properties || {};
      setProfile({
        bio: p.bio || '',
        experience: p.experience || '',
        expertise: p.expertise || [],
        languages: p.languages || [],
        skills: p.skills || [],
      });
    } catch {
      toast('Could not load the partner', { tone: 'error' });
    } finally {
      setLoading(false);
    }
  }, [dispatch, id, toast]);

  useEffect(() => {
    load();
    agentParamVariable(dispatch)
      .then((res) => setParams(res || { expertise: [], languages: [], skills: [] }))
      .catch(() => {});
  }, [load, dispatch]);

  const saveProfile = useCallback(async () => {
    setSavingProfile(true);
    try {
      await agentUpdateDetails(dispatch, id, {
        bio: profile.bio,
        experience: profile.experience ? Number(profile.experience) : null,
        expertise: profile.expertise,
        languages: profile.languages,
        skills: profile.skills,
      });
      toast('Profile updated', { tone: 'success' });
      load();
    } catch {
      toast('Could not update the profile', { tone: 'error' });
    } finally {
      setSavingProfile(false);
    }
  }, [dispatch, id, profile, toast, load]);

  const openService = (service) => {
    if (service) {
      setServiceForm({
        title: service.title || '',
        short_desc: service.short_desc || '',
        long_desc: service.long_desc || '',
        duration: service.duration || 30,
        price: service.price ?? '',
        currency: service.currency || 'INR',
        status: service.status || 'live',
      });
    } else {
      setServiceForm(EMPTY_SERVICE);
    }
    setEditingService(service || 'new');
  };

  const saveService = useCallback(async () => {
    const isNew = editingService === 'new';
    if (!isNew && serviceForm.status !== editingService.status) {
      const ok = await confirm({
        title: `Set “${serviceForm.title}” to ${serviceForm.status.replace(/_/g, ' ')}?`,
        body:
          serviceForm.status === 'live'
            ? 'The service becomes bookable immediately.'
            : 'The service stops being bookable immediately.',
        confirmLabel: 'Change status',
      });
      if (!ok) return;
    }

    setSavingService(true);
    try {
      const payload = { ...serviceForm, duration: Number(serviceForm.duration), price: Number(serviceForm.price) };
      if (isNew) await createAgentService(dispatch, id, payload);
      else await updateAgentServiceDetails(dispatch, editingService.id, payload);
      toast(isNew ? 'Service created' : 'Service updated', { tone: 'success' });
      setEditingService(null);
      load();
    } catch {
      toast('Could not save the service', { tone: 'error' });
    } finally {
      setSavingService(false);
    }
  }, [editingService, serviceForm, confirm, dispatch, id, toast, load]);

  const name = partner?.properties?.name || partner?.email || 'Partner';

  const dirty = useMemo(() => {
    const p = partner?.properties || {};
    return (
      profile.bio !== (p.bio || '') ||
      String(profile.experience) !== String(p.experience || '') ||
      JSON.stringify(profile.expertise) !== JSON.stringify(p.expertise || []) ||
      JSON.stringify(profile.languages) !== JSON.stringify(p.languages || []) ||
      JSON.stringify(profile.skills) !== JSON.stringify(p.skills || [])
    );
  }, [profile, partner]);

  return (
    <>
      <PageHeader
        section="Partners"
        title={name}
        subtitle={partner ? `${partner.email} · ${phone(partner.phone_number)}` : ''}
        back="onboarding"
        backTo="/agent-onboarding"
        actions={partner?.status ? <StatusPill status={partner.status} /> : null}
      />

      <div className="sv-screen-body">
        {loading && (
          <div className="sv-card" style={{ padding: 18 }}>
            <SkeletonBlock width="45%" height={14} />
            <div style={{ marginTop: 12 }}>
              <SkeletonBlock width="80%" height={11} />
            </div>
          </div>
        )}

        {!loading && partner && (
          <div className="sv-partner-profile">
            <section className="sv-card sv-invite__card">
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 18 }}>
                {partner.properties?.profile_image_url ? (
                  <img
                    src={partner.properties.profile_image_url}
                    alt=""
                    style={{ width: 52, height: 52, borderRadius: '50%', objectFit: 'cover' }}
                  />
                ) : (
                  <Avatar name={name} seed={partner.id} size={52} fontSize={20} />
                )}
                <div>
                  <div style={{ fontSize: 15, fontWeight: 600 }}>{name}</div>
                  <div style={{ fontSize: 12.5, color: 'var(--text-muted)' }}>
                    {partner.properties?.gender || '—'}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <Field label="Bio">
                  <Textarea
                    rows={5}
                    value={profile.bio}
                    onChange={(e) => setProfile((p) => ({ ...p, bio: e.target.value }))}
                  />
                </Field>
                <Field label="Experience" hint="Years">
                  <TextInput
                    type="number"
                    value={profile.experience}
                    onChange={(e) => setProfile((p) => ({ ...p, experience: e.target.value }))}
                  />
                </Field>
                <ChipPicker
                  label="Expertise"
                  options={params.expertise || []}
                  value={profile.expertise}
                  onChange={(v) => setProfile((p) => ({ ...p, expertise: v }))}
                />
                <ChipPicker
                  label="Languages"
                  options={params.languages || []}
                  value={profile.languages}
                  onChange={(v) => setProfile((p) => ({ ...p, languages: v }))}
                />
                <ChipPicker
                  label="Skills"
                  options={params.skills || []}
                  value={profile.skills}
                  onChange={(v) => setProfile((p) => ({ ...p, skills: v }))}
                />
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                <Button variant="primary" onClick={saveProfile} loading={savingProfile} disabled={!dirty}>
                  Save profile
                </Button>
              </div>
            </section>

            <section className="sv-card sv-invite__card">
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: 14,
                }}
              >
                <h2 className="sv-invite__card-title" style={{ margin: 0 }}>
                  Services ({services.length})
                </h2>
                <Button variant="secondary" icon="bx-plus" onClick={() => openService(null)}>
                  Add service
                </Button>
              </div>

              {services.length === 0 && (
                <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>No services yet.</p>
              )}

              {services.map((service) => (
                <div className="sv-service-row" key={service.id}>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="sv-service-row__title sv-truncate">{service.title}</div>
                    <div className="sv-service-row__meta">
                      <span className="sv-mono">
                        {service.currency === 'INR' ? '₹' : '$'}
                        {service.price} · {service.duration} min
                      </span>
                    </div>
                  </div>
                  <StatusPill status={service.status} />
                  <IconButton icon="bx-edit" size="sm" label="Edit service" onClick={() => openService(service)} />
                </div>
              ))}
            </section>
          </div>
        )}
      </div>

      <Modal
        open={!!editingService}
        onClose={() => setEditingService(null)}
        title={editingService === 'new' ? 'Add service' : 'Edit service'}
        size="form"
        confirmLabel={editingService === 'new' ? 'Create service' : 'Save changes'}
        confirmLoading={savingService}
        confirmDisabled={!serviceForm.title.trim() || !serviceForm.price}
        onConfirm={saveService}
      >
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, paddingTop: 4 }}>
          <Field label="Title" required className="sv-span-2">
            <TextInput
              value={serviceForm.title}
              onChange={(e) => setServiceForm((f) => ({ ...f, title: e.target.value }))}
            />
          </Field>
          <Field label="Duration" required hint="Minutes">
            <TextInput
              type="number"
              value={serviceForm.duration}
              onChange={(e) => setServiceForm((f) => ({ ...f, duration: e.target.value }))}
            />
          </Field>
          <Field label="Price" required>
            <TextInput
              type="number"
              value={serviceForm.price}
              onChange={(e) => setServiceForm((f) => ({ ...f, price: e.target.value }))}
            />
          </Field>
          <Field label="Currency">
            <Select
              options={CURRENCIES}
              value={serviceForm.currency}
              onChange={(e) => setServiceForm((f) => ({ ...f, currency: e.target.value }))}
            />
          </Field>
          <Field label="Status">
            <Select
              options={SERVICE_STATUSES}
              value={serviceForm.status}
              onChange={(e) => setServiceForm((f) => ({ ...f, status: e.target.value }))}
            />
          </Field>
          <Field label="Short description" className="sv-span-2">
            <TextInput
              value={serviceForm.short_desc}
              onChange={(e) => setServiceForm((f) => ({ ...f, short_desc: e.target.value }))}
            />
          </Field>
          <Field label="Long description" className="sv-span-2">
            <Textarea
              rows={4}
              value={serviceForm.long_desc}
              onChange={(e) => setServiceForm((f) => ({ ...f, long_desc: e.target.value }))}
            />
          </Field>
        </div>
      </Modal>
    </>
  );
}
